import type {HotelDetails} from '@/api/hoteldetails';
import type {DeepPartial} from '@/types';

import {
  HotelDetailsOverlayDefaultConfig,
  type HotelDetailsOverlayConfig,
  type HotelDetailsOverlayData,
  type HotelDetailsOverlayProps,
} from '@travel-frontend/ui-components';
import {merge} from 'lodash';
import {useCallback, useMemo, useState} from 'react';

import {config as appConfig} from '@/config';
import {useHotelDetails, useLocalSplit} from '@/modules/common/hooks';
import {useEnvironment} from '@/modules/common/hooks/useEnvironment';
import {useOverlayState} from '@/modules/common/hooks/useOverlayState';
import {fromGiataToRaw} from '@/modules/common/utils/hotelIdFormat';
import {
  extractHotelDistances,
  hasDistances,
  type Distances,
} from '@/modules/hotels/utils/hotelDistances';

// @FIXME export explicitly from toolkit instead (also the SECTION_* constants)
export type Section = HotelDetailsOverlayProps['activeSection'];

const Sections: Array<Section> = [
  'description',
  'attributes',
  'rating',
  'map',
  'amadeusRating',
] as const;

type Data = Required<HotelDetailsOverlayData>;

const mapHotelDetails = (
  hotelDetails: HotelDetails | null | undefined,
  distances?: Distances,
  isSupplierAttributesFeatureOn = false
): Data | null => {
  // data not loaded yet
  if (hotelDetails == null) {
    return null;
  }

  const {
    name: hotelName,
    address: hotelAddress,
    category: hotelCategory,
    categoryType: hotelCategoryType,
    images,
    attributes: attributeGroups = [],
    badge = '',
    badgeText = '',
    descriptionCurated,
    lat,
    lng,
    isSustainable = false,
    sustainableCertifier = '',
    supplierAttributes,
  } = hotelDetails;

  let descriptionData: Data['description'] = null;
  let attributesData: Data['attributes'] = null;
  let mapData: Data['map'] = null;

  // Create description data if we have curated description OR distances
  if ((descriptionCurated && descriptionCurated?.length > 0) || distances) {
    descriptionData = {
      hotelName,
      hotelAddress: {
        city: hotelAddress?.city ?? '',
        country: hotelAddress?.country ?? '',
        region: hotelAddress?.region ?? '',
        street: hotelAddress?.street ?? '',
        streetNumber: hotelAddress?.streetNumber ?? '',
        zipCode: hotelAddress?.zip ?? '',
      },
      hotelCategory,
      hotelCategoryType,
      hotelImage: images?.[0]?.small?.url || '',
      curatedDescription: descriptionCurated,
      isSustainable,
      sustainableCertifier,
      tourOperatorImage: '',
      distances,
    };
  }

  if (
    (Array.isArray(attributeGroups) && attributeGroups.length > 0) ||
    (isSupplierAttributesFeatureOn && supplierAttributes?.length)
  ) {
    attributesData = {
      attributes: attributeGroups
        .map(({attributes, ...attributeGroup}) => ({
          ...attributeGroup,
          attributes: attributes || [],
        }))
        .filter((attributeGroup) => attributeGroup.attributes.length > 0),
      badge,
      badgeText,
      supplierAttributes: isSupplierAttributesFeatureOn
        ? supplierAttributes
        : undefined,
    };
  }

  if (lat && lng) {
    mapData = {
      latitude: lat,
      longitude: lng,
      hotelName,
    };
  }

  if (descriptionData !== null || attributesData !== null || mapData !== null) {
    return {
      description: descriptionData,
      attributes: attributesData,
      rating: null,
      map: mapData,
      amadeusRating: null, // not part of hotelDetails, so always empty here
    };
  }
  // returning null explicitly here instead of a record with null fields makes checking on the consuming side easier
  return null;
};

export const useHotelDetailsOverlay = ({
  hotelId,
  amadeusTrustedReviewsAvailable,
  onClickReviewImage,
}: {
  hotelId: string;
  amadeusTrustedReviewsAvailable: boolean;
  onClickReviewImage: HotelDetailsOverlayConfig['amadeusRating']['onClickReviewImage'];
}): {
  isOpen: boolean;
  isLoading: boolean;
  activeSection: Section;
  data: Data | null;
  config: HotelDetailsOverlayConfig;
  open: () => void;
  close: () => void;
  changeSection: (section: Section) => void;
} => {
  const {isMobile} = useEnvironment();

  const hotelContentApiSplit = useLocalSplit<
    'off' | 'on' | 'supplierAttributes'
  >('FFP_5733_hotel_content_api');
  const recommendationRateSplit = useLocalSplit(
    'FFP_5186_ama_recommendation_percentage'
  );
  const aiReviewSummarySplit = useLocalSplit<
    'default' | 'off' | 'variation1' | 'variation2'
  >('FBO_3647_review_AIsummary');
  const backButtonBehaviorSplit = useLocalSplit(
    'FFP_4050_browser_back_hoteldetailslayer_close'
  );

  const features = {
    hotelDistances:
      hotelContentApiSplit.treatment === 'on' ||
      hotelContentApiSplit.treatment === 'supplierAttributes',
    supplierAttributes: hotelContentApiSplit.treatment === 'supplierAttributes',
    recommendationRate:
      recommendationRateSplit.treatment === 'on' ||
      recommendationRateSplit.treatment === 'default',
    backButtonBehavior: backButtonBehaviorSplit.treatment === 'on',
  };

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [activeSection, setActiveSection] = useState<Section | null>(null);

  const [hotelDetails] = useHotelDetails(hotelId);

  // Extract and prepare distances from hotel attributes
  const distances = useMemo(() => {
    if (!features.hotelDistances || !hotelDetails?.attributes) {
      return undefined;
    }

    const extractedDistances = extractHotelDistances(hotelDetails.attributes);

    return hasDistances(extractedDistances) ? extractedDistances : undefined;
  }, [hotelDetails?.attributes, features.hotelDistances]);

  const data = useMemo(() => {
    const mappedData = mapHotelDetails(
      hotelDetails,
      distances,
      features.supplierAttributes
    );
    if (amadeusTrustedReviewsAvailable && mappedData !== null) {
      mappedData.amadeusRating = {
        hotelId: fromGiataToRaw(hotelId),
        url: import.meta.env.VITE_SERVICE_REVIEW_SERVICE_URL,
      };
    }
    return mappedData;
  }, [
    features.supplierAttributes,
    amadeusTrustedReviewsAvailable,
    hotelDetails,
    hotelId,
    distances,
  ]);

  // @TODO wrong place
  const overlayState = useOverlayState({
    id: 'hotel-details-overlay',
    enabled: features.backButtonBehavior && isMobile,
    onChange: useCallback(() => {
      setIsOpen(false);
    }, []),
  });

  // initial section depends on async data, also sections might be missing, so pick the first one with data
  const initialActiveSection =
    Sections.find((section) => data?.[section] != null) ?? 'description';

  const config = merge<
    HotelDetailsOverlayConfig,
    DeepPartial<HotelDetailsOverlayConfig>
  >(
    {...HotelDetailsOverlayDefaultConfig},
    {
      map: {
        googleMapsApiOptions: {
          ...appConfig.googleMapsApiOptions,
          googleMapsApiKey: import.meta.env.VITE_SERVICE_GOOGLE_MAPS_KEY,
          language: import.meta.env.VITE_LOCALE,
        },
        googleMapsStyleId: 'c8e746fea1282ca0',
      },
      amadeusRating: {
        recommendationRateEnabled: features.recommendationRate,
        splitTreatment: {
          control: undefined,
          default: undefined,
          off: undefined,
          variation1: 'AI_best_match' as const,
          variation2: 'AI_pros_cons' as const,
        }[aiReviewSummarySplit.treatment],
        reviewSource: appConfig.hotelDetails.reviewSource,
        language: import.meta.env.VITE_LANGUAGE,
        onClickReviewImage,
      },
    }
  );

  return {
    isOpen,
    isLoading: false,
    activeSection: activeSection || initialActiveSection,
    data,
    config,
    open() {
      setIsOpen(true);
      overlayState.store();
    },
    close() {
      setIsOpen(false);
      overlayState.discard();
    },
    changeSection(section) {
      setActiveSection((previous) =>
        // only change section if data is available, otherwise keep the previous one
        data?.[section] === null ? previous : section
      );
    },
  };
};
